import { ContentDifficulty, ContentPlatform } from "../../common/contentTypes";

/**
 * Slack renders at most this many characters of a choice label, and it is the
 * only consumer with a cap — questions over it still serve on Events and Teams
 * (Slack's read path passes the cap and filters). So over-length is a warning
 * here, mirroring the server's soft C2 check, not a blocked save.
 */
export const MAX_CHOICE_LABEL = 29;

export interface ChoiceDraft {
  label: string;
  correct: boolean;
}

export interface QuestionFormState {
  title: string;
  categoryId: string;
  difficulty: ContentDifficulty;
  choices: ChoiceDraft[];
  hiddenOnPlatforms: ContentPlatform[];
}

export interface QuestionFormValidation {
  /** Block the save — mirrors the server's hard checks (C7, C11). */
  errors: string[];
  /** Save proceeds; surfaced in the editor and the lint report (C2, C4). */
  warnings: string[];
}

export function emptyQuestionForm(categoryId = ""): QuestionFormState {
  return {
    title: "",
    categoryId,
    difficulty: "medium",
    choices: [
      { label: "", correct: true },
      { label: "", correct: false },
      { label: "", correct: false },
      { label: "", correct: false },
    ],
    hiddenOnPlatforms: [],
  };
}

/**
 * Counts code points, not UTF-16 units, so the counter agrees with the
 * platform's `contentTextLength` ($strLenCP) — otherwise an emoji-heavy label
 * would read as double its real length.
 */
export function choiceCounterState(label: string): {
  count: number;
  over: boolean;
  /** Within two of the cap — worth colouring before it becomes a problem. */
  near: boolean;
} {
  const count = Array.from(label).length;
  return {
    count,
    over: count > MAX_CHOICE_LABEL,
    near: count > MAX_CHOICE_LABEL - 2 && count <= MAX_CHOICE_LABEL,
  };
}

/** The lint bucket a question was opened from, when it came from Quality. */
export type ContentLintIssue =
  | "overLengthLabels"
  | "duplicateChoices"
  | "duplicateTitles";

/**
 * Whether the specific problem a Quality row was listed for is still there.
 *
 * Opening the editor from Quality is a commitment to fix *that* issue, so it
 * blocks the save while the issue survives — unlike the same editor opened from
 * the questions table, where over-length is only a warning (it still serves on
 * Events and Teams). `original` is the question as it was listed; a duplicate
 * title is only resolved by changing it, which cannot be judged from the form
 * alone.
 */
export function unresolvedIssue(
  form: QuestionFormState,
  issue: ContentLintIssue | undefined,
  original?: { title: string },
): string | undefined {
  if (!issue) {
    return undefined;
  }

  if (issue === "overLengthLabels") {
    const over = form.choices.filter(
      (choice) => choiceCounterState(choice.label).over,
    );
    return over.length > 0
      ? `${over.length} choice label(s) are still over ${MAX_CHOICE_LABEL} characters — shorten them to clear this question from the Quality report`
      : undefined;
  }

  if (issue === "duplicateChoices") {
    const labels = form.choices
      .map((choice) => normalize(choice.label))
      .filter(Boolean);
    return new Set(labels).size !== labels.length
      ? "Two choices are still identical — change one of them to clear this question from the Quality report"
      : undefined;
  }

  return original && normalize(form.title) === normalize(original.title)
    ? "The title is unchanged, so it still duplicates another question — reword it, or delete this copy instead"
    : undefined;
}

const normalize = (label: string) => label.trim().toLowerCase();

export function validateQuestionForm(
  form: QuestionFormState,
): QuestionFormValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!form.title.trim()) {
    errors.push("Title is required");
  }
  if (!form.categoryId) {
    errors.push("Category is required");
  }
  if (form.choices.length !== 4) {
    errors.push("Exactly 4 choices are required");
  }
  if (form.choices.some((choice) => !choice.label.trim())) {
    errors.push("Every choice needs a label");
  }
  if (form.choices.filter((choice) => choice.correct).length !== 1) {
    errors.push("Exactly one choice must be correct");
  }

  const overLength = form.choices.filter(
    (choice) => choiceCounterState(choice.label).over,
  );
  if (overLength.length > 0) {
    warnings.push(
      `${overLength.length} choice label(s) exceed ${MAX_CHOICE_LABEL} characters and will not be served on Slack`,
    );
  }

  const labels = form.choices
    .map((choice) => normalize(choice.label))
    .filter(Boolean);
  if (new Set(labels).size !== labels.length) {
    warnings.push("Two choices have duplicate labels");
  }

  return { errors, warnings };
}
